'use client';

import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/navigation';
import { useCallback } from 'react';
import { useShallow } from 'zustand/react/shallow';
import {
  AnimatedItem,
  AnimatedWrapper,
} from '@/components/animation/AnimatedItem';
import { Menu, type MenuProps } from '@/components/general/Menu';
import { IconComponent } from '@/components/IconComponent';
import { ModalSelectContainer } from '@/components/ModalSelect';
import { SwitchLanguage } from '@/components/SwitchLanguage';
import { NamesSteps } from '@/helpers/auth';
import { generateLink } from '@/helpers/common';
import useEstonia from '@/hooks/estonia';
import { STORAGE, useGetCommonStore, useLocalStorageStore } from '@/store';
import {
  PersistentFormName,
  usePersistentFormsSlice,
} from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import type { ServerLink } from '../../../helpers';
import { AccountInfo } from '../../UserBlock';
import { SignOutButton } from './SignOutButton';
import { ScMenuSwitchLanguage } from './styled';

interface ModalNavProps extends MenuProps {
  locale: string;
  isHidden?: boolean;
  tabBarTitle: string;
  onClose: () => void;
  links: ServerLink[];
}
export const BOTTOM_GAP = 2;

export const ModalNav = ({
  locale,
  isHidden,
  tabBarTitle,
  onClose,
  links,
}: ModalNavProps) => {
  const { push } = useRouter();
  const { setCurrentPersistentStep } = usePersistentFormsSlice();

  const navigate = useCallback(
    (link: string, scrollToTop: boolean) => {
      push(generateLink(link, locale), { scroll: scrollToTop });
    },
    [locale, push]
  );

  const { isEstonia } = useEstonia();
  const useCommonStore = useGetCommonStore();

  const { isAuthenticated, resetUser, tabBarHeight } = useCommonStore(
    useShallow((state) => ({
      isAuthenticated: state.isAuthenticated,
      resetUser: state.resetUser,
      tabBarHeight: state.tabBarHeight,
    }))
  );

  const handleSignOut = useCallback(() => {
    useLocalStorageStore.getState().remove(STORAGE.HAS_SHOWN_LIMIT_MODAL);

    setCurrentPersistentStep(
      PersistentFormName.Registration,
      NamesSteps.PRIMARY_INFORMATION
    );
    onClose();
    resetUser();
    navigate('/', true);

    Sentry.addBreadcrumb({
      category: 'Sign out',
      level: 'info',
    });
  }, [onClose, resetUser, navigate, useLocalStorageStore]);

  return (
    <ModalSelectContainer
      bottom={tabBarHeight - BOTTOM_GAP}
      icon={<IconComponent name="menuIcon" size="40px" />}
      innerAlignBottom={!isAuthenticated}
      isEstonia={isEstonia}
      isHidden={isHidden}
      onClose={onClose}
      padding="16px 0 0 0"
      testId="mobile-menu-modal"
      title={tabBarTitle}
    >
      <AnimatedWrapper>
        <AnimatedItem>
          <AccountInfo onClose={onClose} />
          <Menu
            links={links}
            locale={locale}
            signOutBtn={
              isAuthenticated ? (
                <SignOutButton onSignOut={handleSignOut} />
              ) : null
            }
          />
          <ScMenuSwitchLanguage>
            <SwitchLanguage
              arrowIcon={
                <div className="switchArrow">
                  <IconComponent name="whiteArrowRight" />
                </div>
              }
              onClose={onClose}
              place="header"
            />
          </ScMenuSwitchLanguage>
        </AnimatedItem>
      </AnimatedWrapper>
    </ModalSelectContainer>
  );
};
